import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  NgZone,
  Output,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { auditTime } from 'rxjs/operators';

const SCROLL_POSITION_EPSILON = 1;

@Directive({
  selector: '[fcScrollNearEnd]',
  standalone: true,
})
export class ScrollNearEndDirective implements AfterViewInit, OnDestroy {
  @Input() threshold = 50;
  @Output() nearEnd: EventEmitter<void> = new EventEmitter<void>();
  private subscription = new Subscription();
  private mutationObserver?: MutationObserver;
  private resizeObserver?: ResizeObserver;
  private animationFrameId: number | null = null;
  private lastEmissionKey: string | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    const scrollElement = this.el.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      this.subscription = merge(
        fromEvent(scrollElement, 'scroll', { passive: true }),
        fromEvent(window, 'resize', { passive: true }),
      )
        .pipe(auditTime(50))
        .subscribe(() => this.queueScrollEndCheck());

      if (typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(() =>
          this.queueScrollEndCheck(),
        );
        this.mutationObserver.observe(scrollElement, {
          childList: true,
          subtree: true,
        });
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() =>
          this.queueScrollEndCheck(),
        );
        this.resizeObserver.observe(scrollElement);
      }

      this.queueScrollEndCheck();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  private queueScrollEndCheck(): void {
    if (this.animationFrameId !== null) return;

    this.animationFrameId = requestAnimationFrame(() => {
      this.animationFrameId = null;
      this.checkScrollEnd();
    });
  }

  private checkScrollEnd(): void {
    const state = getScrollNearEndState(this.el.nativeElement, this.threshold);

    if (!state.isNearEnd) {
      this.lastEmissionKey = null;
      return;
    }

    if (state.emissionKey === this.lastEmissionKey) return;

    this.lastEmissionKey = state.emissionKey;
    this.ngZone.run(() => this.nearEnd.emit());
  }
}

export interface ScrollNearEndState {
  isNearEnd: boolean;
  emissionKey: string;
}

export const scrollNearEndListener = <T extends Element>(
  scrollItem: T,
  threshold = 50,
): boolean => {
  return getScrollNearEndState(scrollItem, threshold).isNearEnd;
};

export const getScrollNearEndState = <T extends Element>(
  scrollItem: T,
  threshold = 50,
): ScrollNearEndState => {
  const normalizedThreshold = Math.max(threshold, 0);
  const maxScrollTop = Math.max(
    scrollItem.scrollHeight - scrollItem.clientHeight,
    0,
  );
  const distanceFromEnd = maxScrollTop - scrollItem.scrollTop;

  return {
    isNearEnd: distanceFromEnd <= normalizedThreshold + SCROLL_POSITION_EPSILON,
    emissionKey: `${scrollItem.scrollHeight}:${scrollItem.clientHeight}`,
  };
};
